import { useMemo } from 'react';
import { useBalance } from '~/components/Balance/Balance.hooks';
import { useLevelUpRequirements } from '~/components/Level/Level.hooks';
import { useMember } from '~/components/Member/Member.hooks';
import { useNftCount, usePurchased } from '~/components/Membership/Membership.hooks';

export const useLevelStatuses = () => {
  const {
    data: { isLoggedIn },
  } = useMember();
  const { data: requirements } = useLevelUpRequirements();
  const {
    data: {
      purchase: { amount: purchaseAmount },
    },
    isFetched: isPurchasedFetched,
    isInitialLoading: isPurchasedLoading,
  } = usePurchased({ enabled: isLoggedIn, useErrorBoundary: false });
  const {
    data: { amount: donAmount },
    isFetched: isBalanceFetched,
    isInitialLoading: isBalanceLoading,
  } = useBalance({ enabled: isLoggedIn, useErrorBoundary: false });
  const {
    data: { citizen },
    isFetched: isNftCountFetched,
    isInitialLoading: isNftCountLoading,
  } = useNftCount({ category: 'CITIZEN' }, { enabled: isLoggedIn, useErrorBoundary: false });
  const { countPerLevel = {} } = citizen || {};
  const isFetched = isPurchasedFetched && isBalanceFetched && isNftCountFetched;
  const isLoading = isPurchasedLoading || isBalanceLoading || isNftCountLoading;

  const data = useMemo(
    () =>
      isLoggedIn
        ? Object.entries(requirements).map(([level, required]) => {
            const nftPoint = Math.min(countPerLevel[`lv${level}`] / required.minNftAmount, 1);
            const donPoint = Math.min(donAmount / required.balance.minAmount, 1);
            const purchasedPoint = Math.min(purchaseAmount / required.purchase.minAmount, 1);

            const percentage =
              required.purchase.minAmount === 0
                ? nftPoint * 50 + donPoint * 50
                : nftPoint * 30 + donPoint * 30 + purchasedPoint * 40;

            return {
              level,
              percentage,
              canLevelUp: percentage === 100,
              minNftAmount: required.minNftAmount,
              nftCount: countPerLevel[`lv${level}`],
            } as const;
          })
        : [],
    [countPerLevel, donAmount, isLoggedIn, purchaseAmount, requirements]
  );

  return { isFetched, isLoading, data };
};
